import './Banner.css'

import { clsx } from 'clsx'
import type { FC, ReactElement } from 'react'

import { CheckCircle } from '#design-system/icons/check-circle'
import type { IconProps } from '#design-system/icons/factory'
import { Info } from '#design-system/icons/info'
import { Warning } from '#design-system/icons/warning'
import { WarningCircle } from '#design-system/icons/warning-circle'
import type { SemanticType } from '#design-system/semanticType'

interface BannerProps {
  headerText: string
  bodyText: string
  semanticType: SemanticType
  className?: string
  id?: string
}

const ASSERTIVE_TYPES: ReadonlySet<SemanticType> = new Set(['Warning', 'Error'])

/* Per-semantic status glyph, auto-injected in the header band. */
const SEMANTIC_ICONS: Record<SemanticType, FC<IconProps>> = {
  Info,
  Neutral: Info,
  Success: CheckCircle,
  Warning,
  Error: WarningCircle,
}

export const Banner = ({
  headerText,
  bodyText,
  semanticType,
  className,
  id,
}: BannerProps): ReactElement => {
  const role = ASSERTIVE_TYPES.has(semanticType) ? 'alert' : 'status'
  const SemanticIcon = SEMANTIC_ICONS[semanticType]
  return (
    <section
      className={clsx('doss-banner', className, semanticType.toLowerCase())}
      id={id}
      role={role}
    >
      <h4 className="header">
        <SemanticIcon weight="regular" />
        {headerText}
      </h4>
      <p className="body">{bodyText}</p>
    </section>
  )
}
